import { ThemeProvider } from '@emotion/react';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { createTheme } from '@mui/material/styles';
import * as React from 'react';
import { Alert } from '@mui/material';
import { createOtr } from 'APIs/HotAuctionApi';
import { useAppSelector } from 'app/hooks';
import NormalButton from 'components/atoms/Button/NormalButton';
import DropZoneCards from 'components/molecules/Card/DropZone/DropZoneCards';
import UploadNft from 'components/molecules/CreateNftForm/UploadNft';
import InputText from 'components/molecules/InputText/InputText';
import { selectAuth } from 'features/auth/authSlice';
import { IOtrForm, OtrType } from 'interfaces/OtrData.interface';
import { uploadFileUtil } from 'utils/Fileupload.utils';
import classes from './creationBody.module.css';
import Categories from 'components/molecules/CategoryModal/Categories';

const colortheme = createTheme({
  palette: {
    primary: {
      main: '#292739',
    },
    secondary: {
      main: '#36899f',
    },
  },
  typography: {
    fontFamily: ['lato'].join(','),
  },
});

type OwnAlt = {
  yes: boolean;
  no: boolean;
};
const CreationBody = () => {
  const [success, setSuccess] = React.useState<boolean>(false);
  const [failure, setFailure] = React.useState<boolean>(false);
  const [uploading, setUploading] = React.useState<boolean>(false);
  const [badFile, setBadFile] = React.useState<boolean>(false);
  const [uploadPercentage, setUploadPercentage] = React.useState<number>(0);
  const [categories, setCategories] = React.useState<OtrType[]>([]);
  const [ownData, setOwnData] = React.useState<OwnAlt>({
    yes: true,
    no: false,
  });

  const authState = useAppSelector(selectAuth);
  const [form, setForm] = React.useState<IOtrForm>({
    title: '',
    description: '',
    username: '',
    src_url: '',
    categories: undefined,
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.currentTarget;
    if (name !== 'src_url') {
      setForm({ ...form, [name]: value });
    } else {
      if (files) setForm({ ...form, [name]: files[0] });
    }
  };

  const setYesTrue = (): void => {
    setOwnData({ yes: true, no: false });
  };
  const setNoTrue = (): void => {
    setOwnData({ yes: false, no: true });
  };

  const emptyFunc = () => {};

  const getFiles = (file: File) => {
    setForm({
      ...form,
      src_url: file,
    });
  };

  const onOtrSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (authState.username) {
      try {
        if (form.src_url.size / 1024 / 1024 > 3) {
          setBadFile(true);
          return;
        } else {
          setUploading(true);
          const posterUrl = await uploadFileUtil(
            form.src_url,
            setUploadPercentage,
            false
          );
          form.src_url = posterUrl;
          form.username = authState.username;
          form.categories = categories

          const uploadOtr = await createOtr(form);
          if (uploadOtr.data) {
            setUploading(false);
            setSuccess(true);
            setFailure(false);
            setBadFile(false);
          }
        }
      } catch (error) {
        console.log(error);
        setUploading(false);
        setFailure(true);
        setBadFile(false);
        setSuccess(false);
        setUploadPercentage(0);
      }
    }
  };

  return (
    <React.Fragment>
      <form onSubmit={onOtrSubmit}>
        {uploading ? (
          <UploadNft
            value={uploadPercentage}
            description=""
            uploadingItem="OTR"
          />
        ) : (
          <ThemeProvider theme={colortheme}>
            <Grid container className={classes.body}>
              {success && (
                <Alert
                  sx={{ width: '100%', marginBottom: '20px' }}
                  onClose={() => setSuccess(false)}
                  severity="success"
                >
                  Your OTR has been uploaded successfully.
                </Alert>
              )}
              {failure && (
                <Alert
                  sx={{ width: '100%', marginBottom: '20px' }}
                  onClose={() => setFailure(false)}
                  severity="error"
                >
                  There was an error uploading your OTR.
                </Alert>
              )}
              {badFile && (
                <Alert
                  sx={{ width: '100%', marginBottom: '20px' }}
                  onClose={() => setBadFile(false)}
                  severity="error"
                >
                  Please upload a file smaller than 3MB.
                </Alert>
              )}
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '0px',
                    fontSize: '2rem',
                  }}
                >
                  Create OTR
                </Typography>
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '0px',
                  }}
                >
                  Name
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <InputText
                  onChange={handleChange}
                  initValue={'Item Name'}
                  type={'bookmark'}
                  name="title"
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography className={classes.titleText}>
                  Description
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Tell us a little bit about your OTR.
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <InputText
                  onChange={handleChange}
                  initValue={'Description.....'}
                  type={'grid'}
                  name="description"
                  ablefunction={emptyFunc}
                  unablefunction={emptyFunc}
                  setName={emptyFunc}
                />
              </Grid>

              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '50px',
                  }}
                >
                  Add your content
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Files types supported: JPG, PNG, GIF, SVG, MP4, WEBM, MP3,
                  WAV, OGG, GLB, GLTF. Max size: 100 MB
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <DropZoneCards
                  form={form}
                  setForm={setForm}
                  getFiles={getFiles}
                  onChange={handleChange}
                />
              </Grid>
              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '34px',
                  }}
                >
                  Do you own this audio?
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12}>
                <Typography className={classes.contentText}>
                  Disclaimer: must not contain any copyright audio or the OTR
                  will be taken down.
                </Typography>
              </Grid>
              <Grid className={classes.contentGrid} item xs={12} gap={2}>
                <div onClick={() => setYesTrue()}>
                  <NormalButton
                    text={'Yes'}
                    color={'yellow'}
                    isClicked={ownData.yes}
                  />
                </div>
                <div onClick={() => setNoTrue()}>
                  <NormalButton
                    text={'No'}
                    color={'yellow'}
                    isClicked={ownData.no}
                  />
                </div>
              </Grid>

              <Grid className={classes.paraTextDivGrid} item xs={12}>
                <Typography
                  className={classes.titleText}
                  style={{
                    paddingTop: '50px',
                  }}
                >
                  Set Categories For Your OTR
                </Typography>
              </Grid>

              <Categories
                categories={categories}
                setCategories={setCategories}
              />

              <Grid
                className={classes.contentGrid}
                style={{ width: '100%', marginTop: '40px' }}
              >
                <Button
                  type="submit"
                  onClick={onOtrSubmit}
                  className={classes.createButton}
                >
                  Create OTR
                </Button>
              </Grid>
            </Grid>
          </ThemeProvider>
        )}
      </form>
    </React.Fragment>
  );
};

export default CreationBody;
